/**
 * Anteilsbalken — „wie viel von wie viel".
 *
 * Übernommen aus `components/data/ProgressBar.jsx`. Anders als dort ist der
 * Balken hier für eine Sprachausgabe erreichbar: `role="progressbar"` samt
 * `aria-valuetext`, damit vorgelesen wird, was danebensteht („4,6 kWh"), und
 * nicht die nackte Prozentzahl.
 *
 * Die Farbe kommt von außen und hat keine Vorgabe aus der Marke: Ein Balken ist
 * eine Datendarstellung, dort gilt die Größenpalette (ADR 0013).
 */
import type { ReactNode } from "react";
import { fontWeight, quantityColorVar } from "../tokens.js";

export interface ProgressBarProps {
  readonly label: ReactNode;
  /** Der Wert, in derselben Einheit wie `max`. */
  readonly value: number;
  readonly max?: number;
  /** Der fertig formatierte Wert für die rechte Seite — „4,6 kWh". */
  readonly valueLabel?: string;
  /** Balkenfarbe. Aus `quantityColorVar(...)`, nie aus der Marke. */
  readonly color?: string;
}

export function ProgressBar({
  label,
  value,
  max = 100,
  valueLabel,
  color = quantityColorVar("consumption"),
}: ProgressBarProps) {
  /**
   * Auf 0…100 begrenzt, aber der **Wert** bleibt unangetastet: Ein Balken, der
   * über sein Ende hinausläuft, ist ein Darstellungsproblem; die Zahl daneben
   * ist trotzdem die gemessene und wird nicht beschnitten.
   */
  const percent = max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13 }}>
        <span style={{ color: "var(--text-body)" }}>{label}</span>
        {valueLabel !== undefined && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: fontWeight.semibold,
              color: "var(--text-strong)",
            }}
          >
            {valueLabel}
          </span>
        )}
      </div>
      <div
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuetext={valueLabel}
        style={{
          height: 8,
          borderRadius: "var(--radius-pill)",
          background: "var(--surface-sunken)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            borderRadius: "var(--radius-pill)",
            background: color,
            transition: "width .5s cubic-bezier(.4,0,.2,1)",
          }}
        />
      </div>
    </div>
  );
}
